import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../button/button.js';
import { cn } from '../../../lib/cn.js';

interface ButtonColorfulProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  /** Icon on the right — defaults to ArrowRight */
  endIcon?: ReactNode | false;
  children?: ReactNode;
}

/**
 * Branded "colorful" button — adapted from the 21st `button-colorful` snippet
 * to use the AgentBrush accent palette (lime → cyan → blue). The dark canvas
 * base keeps the chrome looking like our other CTAs, while the gradient blur
 * animates in on hover and lifts the button off the page.
 */
export function ButtonColorful({
  className,
  label,
  endIcon,
  children,
  ...props
}: ButtonColorfulProps) {
  const Icon = endIcon === false ? null : (endIcon ?? <ArrowRight className="h-3.5 w-3.5" />);
  const content = children ?? label;
  return (
    <Button
      className={cn(
        'group relative h-10 cursor-pointer overflow-hidden rounded-md px-4 text-canvas',
        'bg-canvas',
        className,
      )}
      {...props}
    >
      {/* Soft gradient halo — subtle bloom around the pill, intensifies on hover */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -inset-1 rounded-[10px]',
          'bg-[linear-gradient(110deg,_#DDFC74_0%,_#2DE1FC_50%,_#256EFF_100%)]',
          'opacity-50 blur-md transition-opacity duration-300',
          'group-hover:opacity-90',
        )}
      />
      {/* Crisp gradient fill — lime → cyan → blue */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 rounded-md',
          'bg-[linear-gradient(110deg,_#DDFC74_0%,_#2DE1FC_50%,_#256EFF_100%)]',
        )}
      />
      {/* Shine sweep on hover */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 -translate-x-full',
          'bg-gradient-to-r from-transparent via-white/40 to-transparent',
          'transition-transform duration-700 group-hover:translate-x-full',
        )}
      />
      <span className="relative z-10 flex items-center justify-center gap-2 font-semibold">
        {content}
        {Icon}
      </span>
    </Button>
  );
}
